import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { chatAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import {
  MessageCircle,
  Send,
  Paperclip,
  Plus,
  Search,
  ArrowLeft,
  Users,
  Smile,
  X,
  FileText,
  Image as ImageIcon,
  Film,
  Music,
  File,
  Download,
  Check,
  CheckCheck,
  UserPlus,
  Eye,
  Loader2,
  Presentation,
  Sheet,
} from 'lucide-react';
import { toast } from 'sonner';
import { ImageViewer } from '../components/ui/image-viewer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Notification sound — create fresh Audio each time for reliability
const playNotificationSound = () => {
  try {
    const audio = new Audio('/notification.wav?v=3');
    audio.volume = 1.0;
    audio.play().catch(() => {});
  } catch {}
};

const EMOJI_LIST = [
  '😀','😂','😍','🥰','😊','😎','🤔','😅','😢','😭',
  '🤣','😘','🥺','😤','🔥','❤️','👍','👎','🎉','🙏',
  '💪','✅','❌','⭐','💯','👋','🤝','💡','🎯','🚀',
  '📌','⏰','📎','✨','💼','📊','📝','🗓️','☕','🍕',
];

const getInitials = (name) =>
  name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';

const getPhotoUrl = (photo) => {
  if (!photo) return null;
  if (photo.startsWith('http')) return photo;
  return `${BACKEND_URL}${photo}`;
};

const getFileIcon = (type, name) => {
  if (!type && !name) return File;
  const ext = name?.split('.').pop()?.toLowerCase() || '';
  if (type?.startsWith('image/')) return ImageIcon;
  if (type?.startsWith('video/')) return Film;
  if (type?.startsWith('audio/')) return Music;
  if (ext === 'pptx' || ext === 'ppt' || type?.includes('presentation')) return Presentation;
  if (ext === 'xlsx' || ext === 'xls' || type?.includes('spreadsheet') || type?.includes('excel')) return Sheet;
  if (ext === 'pdf' || type?.includes('pdf')) return FileText;
  if (ext === 'docx' || ext === 'doc' || type?.includes('word') || type?.includes('document')) return FileText;
  return File;
};

const getFileCategory = (type, name) => {
  if (!type && !name) return 'other';
  const ext = name?.split('.').pop()?.toLowerCase() || '';
  if (type?.startsWith('image/')) return 'image';
  if (type?.startsWith('video/')) return 'video';
  if (type?.startsWith('audio/')) return 'audio';
  if (ext === 'pdf' || type?.includes('pdf')) return 'pdf';
  if (['pptx','ppt','docx','doc','xlsx','xls'].includes(ext) ||
      type?.includes('presentation') || type?.includes('spreadsheet') ||
      type?.includes('word') || type?.includes('excel') || type?.includes('officedocument'))
    return 'office';
  return 'other';
};

const getFileExtLabel = (name) => {
  const ext = name?.split('.').pop()?.toUpperCase() || '';
  return ext;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  if (isToday) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (isYesterday) return 'Yesterday';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const formatMsgTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// ============ FILE PREVIEW DIALOG ============
const FilePreviewDialog = ({ file, open, onClose }) => {
  if (!file) return null;
  const fileUrl = `${BACKEND_URL}${file.url}`;
  const category = getFileCategory(file.type, file.name);

  const handleDownload = async () => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      window.open(fileUrl, '_blank');
    }
  };

  const getOfficeViewerUrl = () => {
    const publicUrl = encodeURIComponent(fileUrl);
    return `https://view.officeapps.live.com/op/embed.aspx?src=${publicUrl}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] flex flex-col p-0 gap-0 [&>button]:hidden" data-testid="file-preview-dialog">
        {/* Header */}
        <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 border-b border-slate-200 shrink-0 gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {React.createElement(getFileIcon(file.type, file.name), { className: 'w-4 h-4 text-[#1D35A0] shrink-0' })}
            <span className="text-xs sm:text-sm font-medium text-slate-800 truncate">{file.name}</span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Button size="sm" onClick={handleDownload} variant="outline" className="h-8 gap-1 text-xs sm:text-sm px-2 sm:px-3" data-testid="preview-download-btn">
              <Download className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Download</span>
            </Button>
            <Button size="sm" onClick={() => onClose(false)} variant="ghost" className="h-8 w-8 p-0" data-testid="preview-close-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-auto bg-slate-100 flex items-center justify-center">
          {category === 'image' && (
            <img src={fileUrl} alt={file.name} className="max-w-full max-h-[75vh] object-contain" data-testid="preview-image" />
          )}
          {category === 'video' && (
            <video controls autoPlay className="max-w-full max-h-[75vh]" data-testid="preview-video">
              <source src={fileUrl} type={file.type} />
              Your browser does not support video playback.
            </video>
          )}
          {category === 'audio' && (
            <div className="flex flex-col items-center gap-6 py-16 px-8">
              <div className="w-24 h-24 rounded-full bg-[#1D35A0]/10 flex items-center justify-center">
                <Music className="w-12 h-12 text-[#1D35A0]" />
              </div>
              <p className="text-sm font-medium text-slate-700">{file.name}</p>
              <audio controls autoPlay className="w-full max-w-md" data-testid="preview-audio">
                <source src={fileUrl} type={file.type} />
              </audio>
            </div>
          )}
          {category === 'pdf' && (
            <iframe
              src={`${fileUrl}#toolbar=1`}
              title={file.name}
              className="w-full h-[75vh] border-0"
              data-testid="preview-pdf"
            />
          )}
          {category === 'office' && (
            <iframe
              src={getOfficeViewerUrl()}
              title={file.name}
              className="w-full h-[75vh] border-0"
              data-testid="preview-office"
            />
          )}
          {category === 'other' && (
            <div className="flex flex-col items-center gap-4 py-16 text-slate-500">
              <File className="w-16 h-16 text-slate-300" />
              <p className="text-sm">Preview not available for this file type</p>
              <Button onClick={handleDownload} className="bg-[#1D35A0] hover:bg-[#152A80] text-white gap-2">
                <Download className="w-4 h-4" /> Download File
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============ CONVERSATION LIST ============
const ConversationList = ({ conversations, activeId, onSelect, onNewChat, searchTerm, setSearchTerm, currentUser }) => {
  const filtered = conversations.filter(c => {
    const name = c.type === 'group'
      ? c.name
      : c.participants?.find(p => p.id !== currentUser.id)?.full_name || 'Unknown';
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="flex flex-col h-full" data-testid="conversation-list-panel">
      <div className="px-4 pt-4 pb-2 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-800">Conversations</h2>
        <Button
          size="sm"
          onClick={onNewChat}
          className="bg-[#1D35A0] hover:bg-[#152A80] text-white h-8 w-8 p-0"
          data-testid="new-chat-btn"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <div className="px-4 pb-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-9 h-9 text-sm"
            data-testid="search-conversations"
          />
        </div>
      </div>
      <ScrollArea className="flex-1">
        <div className="px-2 pb-2 space-y-0.5">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">
              {searchTerm ? 'No matches found' : 'No conversations yet'}
            </div>
          ) : (
            filtered.map(conv => {
              const isGroup = conv.type === 'group';
              const other = !isGroup ? conv.participants?.find(p => p.id !== currentUser.id) : null;
              const displayName = isGroup ? conv.name : (other?.full_name || 'Unknown');
              const isActive = conv.id === activeId;

              return (
                <button
                  key={conv.id}
                  onClick={() => onSelect(conv)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                    isActive ? 'bg-[#1D35A0]/10 border border-[#1D35A0]/20' : 'hover:bg-slate-100'
                  }`}
                  data-testid={`conv-${conv.id}`}
                >
                  {isGroup ? (
                    <div className="w-10 h-10 rounded-full bg-[#F5AB00]/20 flex items-center justify-center shrink-0">
                      <Users className="w-5 h-5 text-[#F5AB00]" />
                    </div>
                  ) : (
                    <Avatar className="w-10 h-10 shrink-0">
                      <AvatarImage src={getPhotoUrl(other?.profile_photo)} />
                      <AvatarFallback className="bg-[#1D35A0] text-white text-xs">
                        {getInitials(other?.full_name)}
                      </AvatarFallback>
                    </Avatar>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-medium truncate ${isActive ? 'text-[#1D35A0]' : 'text-slate-800'}`}>
                        {displayName}
                      </span>
                      <span className="text-[10px] text-slate-400 shrink-0 ml-2">
                        {formatTime(conv.last_message_at)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                      <p className="text-xs text-slate-500 truncate pr-2">
                        {conv.last_message || 'No messages yet'}
                      </p>
                      {conv.unread_count > 0 && (
                        <Badge className="bg-[#1D35A0] text-white text-[10px] h-5 min-w-[20px] flex items-center justify-center shrink-0">
                          {conv.unread_count}
                        </Badge>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

// ============ MESSAGE BUBBLE ============
const MessageBubble = ({ message, isOwn, currentUser, onPreviewFile }) => {
  const hasFile = message.file && message.file.url;
  const category = hasFile ? getFileCategory(message.file.type, message.file.name) : null;
  const FileIconComp = hasFile ? getFileIcon(message.file.type, message.file.name) : File;
  const fileUrl = hasFile ? `${BACKEND_URL}${message.file.url}` : '';

  const handleDownload = async (e) => {
    e.stopPropagation();
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = message.file.name || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      window.open(fileUrl, '_blank');
    }
  };

  const extLabel = hasFile ? getFileExtLabel(message.file.name) : '';
  const extColors = {
    PDF: 'bg-red-500', DOCX: 'bg-blue-600', DOC: 'bg-blue-600',
    XLSX: 'bg-green-600', XLS: 'bg-green-600',
    PPTX: 'bg-orange-500', PPT: 'bg-orange-500',
    TXT: 'bg-slate-500', MP4: 'bg-purple-600', MP3: 'bg-pink-500',
  };

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2.5`} data-testid={`msg-${message.id}`}>
      <div className={`max-w-[78%] sm:max-w-[60%]`}>
        {!isOwn && (
          <p className="text-[10px] text-slate-400 mb-0.5 ml-1 font-medium">{message.sender_name}</p>
        )}
        <div className={`rounded-2xl px-3.5 py-2 shadow-sm ${
          isOwn
            ? 'bg-[#1D35A0] text-white rounded-br-sm'
            : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm'
        }`}>
          {hasFile && (
            <div className={`${message.text ? 'mb-2' : 'mb-0.5'}`}>
              {/* Image preview */}
              {category === 'image' && (
                <div className="relative rounded-lg overflow-hidden cursor-pointer group/img" onClick={() => onPreviewFile(message.file)}>
                  <img
                    src={fileUrl}
                    alt={message.file.name}
                    className="rounded-lg max-h-52 max-w-[280px] object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover/img:opacity-100">
                    <Eye className="w-8 h-8 text-white drop-shadow-lg" />
                  </div>
                </div>
              )}
              {/* Video inline player */}
              {category === 'video' && (
                <div className="rounded-lg overflow-hidden">
                  <video controls className="rounded-lg max-h-52 max-w-[320px] w-full" preload="metadata">
                    <source src={fileUrl} type={message.file.type} />
                  </video>
                </div>
              )}
              {/* Audio inline player */}
              {category === 'audio' && (
                <div className="py-1">
                  <audio controls className="w-full max-w-[260px] h-9" preload="metadata" style={{ filter: isOwn ? 'invert(1) brightness(2)' : 'none' }}>
                    <source src={fileUrl} type={message.file.type} />
                  </audio>
                </div>
              )}
              {/* Document file card (PDF, Office, Other) */}
              {(category === 'pdf' || category === 'office' || category === 'other') && (
                <div className={`flex items-center gap-2.5 p-2.5 rounded-xl ${
                  isOwn ? 'bg-white/10' : 'bg-slate-50 border border-slate-100'
                }`}>
                  <div className={`w-10 h-10 rounded-lg ${extColors[extLabel] || 'bg-slate-500'} flex items-center justify-center shrink-0`}>
                    <span className="text-[9px] font-bold text-white leading-none">{extLabel}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-xs font-medium truncate ${isOwn ? 'text-white' : 'text-slate-700'}`}>
                      {message.file.name}
                    </p>
                    <p className={`text-[10px] mt-0.5 ${isOwn ? 'text-white/50' : 'text-slate-400'}`}>
                      {formatFileSize(message.file.size)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); onPreviewFile(message.file); }}
                      className={`p-1.5 rounded-lg transition-colors ${
                        isOwn ? 'hover:bg-white/20 text-white/70 hover:text-white' : 'hover:bg-slate-200 text-slate-500 hover:text-[#1D35A0]'
                      }`}
                      title="Preview"
                      data-testid="file-preview-btn"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleDownload}
                      className={`p-1.5 rounded-lg transition-colors ${
                        isOwn ? 'hover:bg-white/20 text-white/70 hover:text-white' : 'hover:bg-slate-200 text-slate-500 hover:text-[#1D35A0]'
                      }`}
                      title="Download"
                      data-testid="file-download-btn"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
              {/* Eye + Download for images (shown below thumbnail) */}
              {category === 'image' && (
                <div className="flex items-center gap-1 mt-1.5">
                  <button
                    onClick={(e) => { e.stopPropagation(); onPreviewFile(message.file); }}
                    className={`p-1 rounded transition-colors ${
                      isOwn ? 'hover:bg-white/20 text-white/60 hover:text-white' : 'hover:bg-slate-100 text-slate-400 hover:text-[#1D35A0]'
                    }`}
                    title="View full size"
                    data-testid="img-preview-btn"
                  >
                    <Eye className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={handleDownload}
                    className={`p-1 rounded transition-colors ${
                      isOwn ? 'hover:bg-white/20 text-white/60 hover:text-white' : 'hover:bg-slate-100 text-slate-400 hover:text-[#1D35A0]'
                    }`}
                    title="Download"
                    data-testid="img-download-btn"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  <span className={`text-[10px] ml-1 ${isOwn ? 'text-white/40' : 'text-slate-400'}`}>
                    {message.file.name}
                  </span>
                </div>
              )}
              {/* Eye + Download for video */}
              {category === 'video' && (
                <div className="flex items-center gap-1 mt-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); onPreviewFile(message.file); }}
                    className={`p-1 rounded transition-colors ${
                      isOwn ? 'hover:bg-white/20 text-white/60 hover:text-white' : 'hover:bg-slate-100 text-slate-400 hover:text-[#1D35A0]'
                    }`}
                    title="Open fullscreen"
                  >
                    <Eye className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={handleDownload}
                    className={`p-1 rounded transition-colors ${
                      isOwn ? 'hover:bg-white/20 text-white/60 hover:text-white' : 'hover:bg-slate-100 text-slate-400 hover:text-[#1D35A0]'
                    }`}
                    title="Download"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  <span className={`text-[10px] ml-1 ${isOwn ? 'text-white/40' : 'text-slate-400'}`}>
                    {message.file.name}
                  </span>
                </div>
              )}
            </div>
          )}
          {message.text && (
            <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{message.text}</p>
          )}
          <div className={`flex items-center justify-end gap-1 mt-0.5 ${isOwn ? 'text-white/40' : 'text-slate-400'}`}>
            <span className="text-[10px]">{formatMsgTime(message.created_at)}</span>
            {isOwn && (
              message.read_by?.length > 1
                ? <CheckCheck className="w-3 h-3 text-sky-300" />
                : <Check className="w-3 h-3" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ CHAT VIEW ============
const ChatView = ({ conversation, currentUser, onBack, onRefreshList }) => {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showEmoji, setShowEmoji] = useState(false);
  const [typingUser, setTypingUser] = useState('');
  const [previewFile, setPreviewFile] = useState(null);
  const [imageViewFile, setImageViewFile] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const emojiRef = useRef(null);

  const isGroup = conversation.type === 'group';
  const other = !isGroup ? conversation.participants?.find(p => p.id !== currentUser.id) : null;
  const displayName = isGroup ? conversation.name : (other?.full_name || 'Unknown');
  const subtitle = isGroup
    ? `${conversation.participants?.length || 0} members`
    : (other?.designation || other?.email || '');

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);


  // Fetch messages
  const fetchMessages = useCallback(async () => {
    try {
      const res = await chatAPI.getMessages(conversation.id);
      setMessages(res.data);
    } catch (e) {
      console.error('Failed to fetch messages', e);
    } finally {
      setLoading(false);
    }
  }, [conversation.id]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // WebSocket connection
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const wsProtocol = BACKEND_URL.startsWith('https') ? 'wss' : 'ws';
    const wsHost = BACKEND_URL.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/api/chat/ws/chat/${token}`;

    let ws;
    try {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'new_message' && data.conversation_id === conversation.id) {
          setMessages(prev => {
            if (prev.find(m => m.id === data.message.id)) return prev;
            return [...prev, data.message];
          });
          // Play notification sound for messages from others
          if (data.message.sender_id !== currentUser.id) {
            playNotificationSound();
          }
          onRefreshList();
        } else if (data.type === 'typing' && data.conversation_id === conversation.id) {
          setTypingUser(data.user_name);
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => setTypingUser(''), 2000);
        } else if (data.type === 'conversation_created') {
          onRefreshList();
        }
      };
    } catch (e) {
      console.error('WebSocket connection failed', e);
    }

    return () => {
      if (ws && ws.readyState === WebSocket.OPEN) ws.close();
    };
  }, [conversation.id, onRefreshList]);

  const sendTyping = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing', conversation_id: conversation.id }));
    }
  };

  const handleSend = async () => {
    if (!text.trim() && !uploading) return;
    setSending(true);
    try {
      const res = await chatAPI.sendMessage(conversation.id, { text: text.trim() });
      // Only add if not already from WS
      setMessages(prev => {
        if (prev.find(m => m.id === res.data.id)) return prev;
        return [...prev, res.data];
      });
      setText('');
      if (textareaRef.current) textareaRef.current.style.height = '40px';
      setShowEmoji(false);
      onRefreshList();
    } catch (e) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    } else {
      sendTyping();
    }
  };

  const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB chunks

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      let fileData;

      if (file.size > CHUNK_SIZE) {
        // Chunked upload for large files
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        const initRes = await chatAPI.initChunkedUpload(conversation.id, {
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
          total_size: file.size,
          total_chunks: totalChunks,
        });
        const uploadId = initRes.data.upload_id;

        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunk = file.slice(start, end);
          await chatAPI.uploadChunk(conversation.id, uploadId, i, chunk);
          setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
        }

        const completeRes = await chatAPI.completeChunkedUpload(conversation.id, uploadId);
        fileData = completeRes.data;
        setUploadProgress(0);
      } else {
        // Direct upload for small files
        const uploadRes = await chatAPI.uploadFile(conversation.id, file);
        fileData = uploadRes.data;
      }

      const res = await chatAPI.sendMessage(conversation.id, {
        text: '',
        file: fileData,
      });
      setMessages(prev => {
        if (prev.find(m => m.id === res.data.id)) return prev;
        return [...prev, res.data];
      });
      onRefreshList();
      toast.success('File sent');
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error('Failed to upload file');
      setUploadProgress(0);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Close emoji picker on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) {
        setShowEmoji(false);
      }
    };
    if (showEmoji) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showEmoji]);

  // Date separator logic
  const getDateLabel = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return 'Today';
    const yd = new Date(now);
    yd.setDate(yd.getDate() - 1);
    if (d.toDateString() === yd.toDateString()) return 'Yesterday';
    return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  };

  let lastDate = '';

  return (
    <div className="flex flex-col h-full" data-testid="chat-view">
      {/* Chat header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 bg-white shrink-0">
        <button onClick={onBack} className="lg:hidden p-1 hover:bg-slate-100 rounded-lg" data-testid="chat-back-btn">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        {isGroup ? (
          <div className="w-10 h-10 rounded-full bg-[#F5AB00]/20 flex items-center justify-center shrink-0">
            <Users className="w-5 h-5 text-[#F5AB00]" />
          </div>
        ) : (
          <Avatar className="w-10 h-10 shrink-0">
            <AvatarImage src={getPhotoUrl(other?.profile_photo)} />
            <AvatarFallback className="bg-[#1D35A0] text-white text-xs">{getInitials(other?.full_name)}</AvatarFallback>
          </Avatar>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-800 truncate">{displayName}</p>
          <p className="text-xs text-slate-500 truncate">{typingUser ? `${typingUser} is typing...` : subtitle}</p>
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-3 bg-slate-50/50" data-testid="messages-area">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1D35A0]"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-400">
            <MessageCircle className="w-12 h-12 mb-3 text-slate-300" />
            <p className="text-sm">No messages yet. Say hello!</p>
          </div>
        ) : (
          messages.map((msg, i) => {
            const dateLabel = getDateLabel(msg.created_at);
            let showDate = false;
            if (dateLabel !== lastDate) {
              showDate = true;
              lastDate = dateLabel;
            }
            return (
              <React.Fragment key={msg.id}>
                {showDate && (
                  <div className="flex items-center justify-center my-3">
                    <span className="text-[10px] bg-slate-200 text-slate-500 px-3 py-0.5 rounded-full">{dateLabel}</span>
                  </div>
                )}
                <MessageBubble
                  message={msg}
                  isOwn={msg.sender_id === currentUser.id}
                  currentUser={currentUser}
                  onPreviewFile={(file) => {
                    const cat = getFileCategory(file.type, file.name);
                    if (cat === 'image') {
                      setImageViewFile(file);
                    } else {
                      setPreviewFile(file);
                    }
                  }}
                />
              </React.Fragment>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="px-3 py-2 border-t border-slate-200 bg-white shrink-0" data-testid="chat-input-area">
          <div className="flex items-end gap-1.5 sm:gap-2">
            <div className="relative" ref={emojiRef}>
              <button
                onClick={() => setShowEmoji(!showEmoji)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-500 hover:text-[#F5AB00]"
                data-testid="emoji-btn"
              >
                <Smile className="w-5 h-5" />
              </button>
              {showEmoji && (
                <div className="absolute bottom-12 left-0 bg-white border border-slate-200 rounded-xl shadow-xl p-3 w-[260px] z-50" data-testid="emoji-picker">
                  <div className="grid grid-cols-8 gap-1">
                    {EMOJI_LIST.map((emoji, i) => (
                      <button
                        key={i}
                        onClick={() => { setText(prev => prev + emoji); }}
                        className="w-7 h-7 flex items-center justify-center hover:bg-slate-100 rounded text-lg"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-500 hover:text-[#1D35A0]"
              disabled={uploading}
              data-testid="attach-file-btn"
            >
              <Paperclip className="w-5 h-5" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={handleFileUpload}
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
            />

            <div className="flex-1 relative">
              <textarea
                ref={textareaRef}
                value={text}
                onChange={e => {
                  setText(e.target.value);
                  if (textareaRef.current) {
                    textareaRef.current.style.height = 'auto';
                    textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 160) + 'px';
                  }
                }}
                onKeyDown={handleKeyDown}
                placeholder="Type a message..."
                rows={1}
                className="w-full resize-none rounded-xl border border-slate-200 bg-slate-50 px-3 sm:px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D35A0]/30 focus:border-[#1D35A0] overflow-y-auto"
                style={{ minHeight: '40px', maxHeight: '160px' }}
                data-testid="message-input"
              />
            </div>

            <Button
              onClick={handleSend}
              disabled={sending || !text.trim()}
              className="bg-[#1D35A0] hover:bg-[#152A80] text-white h-10 w-10 p-0 rounded-xl shrink-0"
              data-testid="send-msg-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        {uploading && (
          <div className="mt-1.5 flex items-center gap-2 text-xs text-[#1D35A0]">
            <Loader2 className="w-3 h-3 animate-spin" />
            {uploadProgress > 0 ? `Uploading... ${uploadProgress}%` : 'Uploading file...'}
            {uploadProgress > 0 && (
              <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden max-w-[120px]">
                <div className="h-full bg-[#1D35A0] rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* File Preview Dialog */}
      <FilePreviewDialog file={previewFile} open={!!previewFile} onClose={() => setPreviewFile(null)} />

      {/* Image Viewer Overlay */}
      {imageViewFile && (
        <ImageViewer
          src={`${BACKEND_URL}${imageViewFile.url}`}
          alt={imageViewFile.name}
          onClose={() => setImageViewFile(null)}
        />
      )}
    </div>
  );
};

// ============ NEW CONVERSATION DIALOG ============
const NewChatDialog = ({ open, onClose, onCreated, currentUser }) => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [isGroup, setIsGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [creating, setCreating] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);

  useEffect(() => {
    if (open) {
      setSearch('');
      setSelectedIds([]);
      setIsGroup(false);
      setGroupName('');
      chatAPI.getChatUsers().then(res => {
        setUsers(res.data);
        setLoadingUsers(false);
      }).catch(() => setLoadingUsers(false));
    }
  }, [open]);

  const filtered = users.filter(u =>
    u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const toggleUser = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleCreate = async () => {
    if (selectedIds.length === 0) return;
    if (isGroup && !groupName.trim()) {
      toast.error('Please enter a group name');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        type: isGroup ? 'group' : 'individual',
        participant_ids: selectedIds,
        name: isGroup ? groupName.trim() : '',
      };
      const res = await chatAPI.createConversation(payload);
      onCreated(res.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create conversation');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1D35A0]">
            <MessageCircle className="w-5 h-5" />
            New Conversation
          </DialogTitle>
          <DialogDescription>Select team members to start a conversation</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 flex-1 min-h-0 flex flex-col">
          {/* Group toggle */}
          <div className="flex items-center justify-between shrink-0">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={isGroup}
                onCheckedChange={setIsGroup}
                data-testid="group-chat-toggle"
              />
              <span className="text-sm text-slate-700">Create group chat</span>
            </label>
            {selectedIds.length > 0 && (
              <Badge variant="outline" className="text-xs">{selectedIds.length} selected</Badge>
            )}
          </div>

          {isGroup && (
            <Input
              placeholder="Group name..."
              value={groupName}
              onChange={e => setGroupName(e.target.value)}
              className="h-9 text-sm shrink-0"
              data-testid="group-name-input"
            />
          )}

          <div className="relative shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search people..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 h-9 text-sm"
              data-testid="search-users-input"
            />
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto border border-slate-100 rounded-lg">
            <div className="space-y-1 p-1">
              {loadingUsers ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#1D35A0]"></div>
                </div>
              ) : filtered.length === 0 ? (
                <p className="text-center py-4 text-sm text-slate-400">No users found</p>
              ) : (
                filtered.map(u => {
                  const selected = selectedIds.includes(u.id);
                  return (
                    <button
                      key={u.id}
                      onClick={() => {
                        if (!isGroup) {
                          setSelectedIds([u.id]);
                        } else {
                          toggleUser(u.id);
                        }
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                        selected ? 'bg-[#1D35A0]/10 border border-[#1D35A0]/20' : 'hover:bg-slate-50'
                      }`}
                      data-testid={`user-${u.id}`}
                    >
                      <Avatar className="w-9 h-9 shrink-0">
                        <AvatarImage src={getPhotoUrl(u.profile_photo)} />
                        <AvatarFallback className="bg-[#1D35A0] text-white text-xs">
                          {getInitials(u.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{u.full_name}</p>
                        <p className="text-xs text-slate-400 truncate">
                          {u.designation || u.role} &middot; {u.company === 'empact_studio' ? 'Empact' : u.company === 'event_resource' ? 'ER' : u.company}
                        </p>
                      </div>
                      {selected && (
                        <Check className="w-4 h-4 text-[#1D35A0] shrink-0" />
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <Button
            onClick={handleCreate}
            disabled={selectedIds.length === 0 || creating || (isGroup && !groupName.trim())}
            className="w-full bg-[#1D35A0] hover:bg-[#152A80] text-white"
            data-testid="create-conversation-btn"
          >
            {creating ? 'Creating...' : isGroup ? 'Create Group' : 'Start Chat'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============ MAIN CONVERSATIONS PAGE ============
export default function Conversations() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await chatAPI.getConversations();
      setConversations(res.data);
    } catch (e) {
      console.error('Failed to fetch conversations', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
    // Clear app badge when user opens conversations
    if ('clearAppBadge' in navigator) {
      navigator.clearAppBadge().catch(() => {});
    }
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  // Global WebSocket for notifications when browsing conversation list
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const wsProtocol = BACKEND_URL.startsWith('https') ? 'wss' : 'ws';
    const wsHost = BACKEND_URL.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/api/chat/ws/chat/${token}`;

    let ws;
    try {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'new_message') {
          // Play sound if message is not from me and not currently viewing that conversation
          if (data.message.sender_id !== user?.id) {
            if (!activeConv || activeConv.id !== data.conversation_id) {
              playNotificationSound();
            }
          }
          fetchConversations();
        }
      };
    } catch (e) {
      console.error('Global WS failed', e);
    }

    return () => {
      if (ws && ws.readyState === WebSocket.OPEN) ws.close();
    };
  }, [user?.id, fetchConversations, activeConv]);

  const handleSelectConv = (conv) => {
    setActiveConv(conv);
  };

  const handleNewChatCreated = (conv) => {
    setActiveConv(conv);
    fetchConversations();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[70vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1D35A0]"></div>
      </div>
    );
  }

  return (
    <div data-testid="conversations-page">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" style={{ height: 'calc(100vh - 120px)' }}>
        <div className="flex h-full">
          {/* Left Panel - Conversation List */}
          <div className={`w-full lg:w-80 lg:border-r border-slate-200 shrink-0 ${activeConv ? 'hidden lg:flex lg:flex-col' : 'flex flex-col'}`}>
            <ConversationList
              conversations={conversations}
              activeId={activeConv?.id}
              onSelect={handleSelectConv}
              onNewChat={() => setShowNewChat(true)}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              currentUser={user}
            />
          </div>

          {/* Right Panel - Chat or Empty State */}
          <div className={`flex-1 flex flex-col ${!activeConv ? 'hidden lg:flex' : 'flex'}`}>
            {activeConv ? (
              <ChatView
                key={activeConv.id}
                conversation={activeConv}
                currentUser={user}
                onBack={() => setActiveConv(null)}
                onRefreshList={fetchConversations}
              />
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-slate-400 bg-slate-50/50" data-testid="no-chat-selected">
                <div className="w-20 h-20 rounded-full bg-[#1D35A0]/5 flex items-center justify-center mb-4">
                  <MessageCircle className="w-10 h-10 text-[#1D35A0]/30" />
                </div>
                <p className="text-base font-medium text-slate-500">Select a conversation</p>
                <p className="text-sm text-slate-400 mt-1">Choose from the list or start a new chat</p>
                <Button
                  onClick={() => setShowNewChat(true)}
                  className="mt-4 bg-[#1D35A0] hover:bg-[#152A80] text-white"
                  data-testid="empty-new-chat-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> New Conversation
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <NewChatDialog
        open={showNewChat}
        onClose={() => setShowNewChat(false)}
        onCreated={handleNewChatCreated}
        currentUser={user}
      />
    </div>
  );
}
